import { useEffect, useRef, useState } from "react";
import { API, api, localDate } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Send, Radio, ImagePlus, RefreshCw, Download, Share2, FolderOpen } from "lucide-react";
import VoiceButton from "@/components/VoiceButton";
import VoiceConversation from "@/components/VoiceConversation";
import VoicePicker from "@/components/VoicePicker";
import { Link } from "react-router-dom";
import { VoiceSample } from "@/components/VoiceSample";
import { toast } from "sonner";
import { LimitDialog } from "@/components/LimitDialog";
import { CoachUpsellNote } from "@/components/CoachUpsellNote";
import { ServicePicker } from "@/components/ServicePicker";

const SWITCH_RE = /\b(switch|change|different|another|new)\b.*\b(service|path|idea|business)\b/i;

const clean = (t) => (t || "").replace(/\*\*(.*?)\*\*/g, "$1").replace(/^#+\s*/gm, "").replace(/\s[—–]\s/g, ". ").replace(/[—–]/g, ", ");

const QUICK = [
  "What do I do next?",
  "What should I post today?",
  "Write me a Facebook caption",
  "Give me 10 TikTok ideas",
  "Give me a business name",
  "Help me make my first $1,000",
  "Write my landing page",
  "Give me hashtags",
];

const REVIEW_MARK = "\u001e";
const PRO_CHAPTER_MSG = "Full book and ebook chapters are part of Pro. Here's a short sample to get you started. Upgrade and your coach writes the whole chapter.";
const finalText = (t) => (t.includes(REVIEW_MARK) ? (t.split(REVIEW_MARK)[1] || t.split(REVIEW_MARK)[0]) : t);
const reviewing = (t) => t.includes(REVIEW_MARK) && !t.split(REVIEW_MARK)[1];

export default function Coach() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState("");
  const [handsFree, setHandsFree] = useState(false);
  const [usage, setUsage] = useState(null);
  const [blocked, setBlocked] = useState(null); // friendly limit message when set
  const scrollRef = useRef();
  const resumeRef = useRef(null); // message to resend after upgrading
  const [historyReady, setHistoryReady] = useState(false);
  const [sentCount, setSentCount] = useState(0); // replies this visit; drives the occasional upgrade note
  const [localPath, setLocalPath] = useState(false);
  const [service, setService] = useState(null);
  const [switchAsked, setSwitchAsked] = useState(false);
  const [charMode, setCharMode] = useState(false);
  useEffect(() => { api.get("/work-extras").then(r => setCharMode(!!r.data.show_character)).catch(() => {}); }, []);
  const wantsCharImage = (q) => charMode && /\b(image|picture|photo|pic|selfie|portrait)\b/i.test(q) && /\b(make|create|generate|draw|show|give|do|need|want)\b/i.test(q);
  const saveAsCharacter = async (dataUrl) => {
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData();
      fd.append("file", blob, "character.png");
      await api.post("/work-extras/character-image", fd);
      toast.success("Saved as your character picture in My Work.");
    } catch { toast.error("Couldn't save that picture. Try again."); }
  };

  useEffect(() => {
    api.get("/progress").then(r => {
      if (r.data.progress?.chosen_path !== "local-service") return;
      setLocalPath(true);
      api.get("/local-service").then(x => setService(x.data.service)).catch(() => {});
    }).catch(() => {});
  }, []);

  const loadUsage = () => api.get("/coach/usage").then(r => setUsage(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/coach/welcome").catch(() => {})
      .finally(() => api.get("/coach/history").then(r => setMessages(r.data.messages || [])).catch(() => {}).finally(() => setHistoryReady(true)));
    loadUsage();
  }, [handsFree]);

  useEffect(() => {
    const pending = sessionStorage.getItem("ihmj_resume");
    if (pending && user?.is_premium) { sessionStorage.removeItem("ihmj_resume"); send(pending); }
    // eslint-disable-next-line
  }, [user?.is_premium]);

  // One-tap opener from the dashboard: /coach?start=<message>
  const startedRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const opener = params.get("start");
    if (!opener || !historyReady || startedRef.current) return;
    startedRef.current = true;
    window.history.replaceState(null, "", "/coach");
    send(opener.slice(0, 300));
    // eslint-disable-next-line
  }, [historyReady]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [messages, streaming]);

  const wantsMeme = (t) => {
    const s = (t || "").toLowerCase();
    if (!/\bmemes?\b/.test(s)) return false;
    if (/\bmemes?\s+(idea|ideas|caption|captions|text|script|scripts)\b/.test(s)) return false;
    return /\b(make|create|generate|build|draw|design|whip|cook|give|show|want|need|another|do|send|get|turn)\b/.test(s) || s.trim().startsWith("meme");
  };

  const generateMeme = async (idea, bubble, character = false) => {
    if (sending) return;
    setSending(true);
    setMessages(m => [...m, { role: "user", content: bubble }]);
    try {
      const r = await api.post("/coach/image", { prompt: idea, character });
      setMessages(m => [...m, { role: "assistant", content: r.data.caption || (character ? "Here's your character." : "Here's your meme, grab it below."), image: r.data.image, prompt: idea, character }]);
      loadUsage();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setMessages(m => m.slice(0, -1));
      if (status === 402) setBlocked(typeof detail === "string" ? detail : "You've used your free coach image. Go Pro for full access.");
      else toast.error(detail || "Couldn't make that image. Try again.");
    } finally { setSending(false); }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    if (wantsCharImage(q)) {
      setInput("");
      await generateMeme(q, q, true);
      return;
    }
    if (wantsMeme(q)) {
      setInput("");
      await generateMeme(q, q);
      return;
    }
    setInput("");
    setSending(true);
    setStreaming("");
    setMessages(m => [...m, { role: "user", content: q }]);
    try {
      const token = localStorage.getItem("ihmj_token");
      const res = await fetch(`${API}/coach`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "X-Local-Date": localDate() },
        body: JSON.stringify({ message: q }),
      });
      if (res.status === 402) {
        const d = await res.json().catch(() => ({}));
        setMessages(m => m.slice(0, -1)); // remove the unsent user bubble
        resumeRef.current = q;
        setBlocked(typeof d.detail === "string" ? d.detail : "You've reached today's limit. Your coach is back tomorrow.");
        if (!user?.is_premium) setUsage(u => ({ ...(u || {}), is_premium: false, free_limit: u?.free_limit ?? 15, messages_remaining: 0 }));
        return;
      }
      if (!res.ok || !res.body) throw new Error("stream failed");
      const proFeature = res.headers.get("X-Pro-Feature");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setStreaming(acc);
      }
      setMessages(m => [...m, { role: "assistant", content: finalText(acc) }]);
      setStreaming("");
      setSentCount(c => c + 1);
      if (SWITCH_RE.test(q)) setSwitchAsked(true);
      loadUsage();
      if (proFeature) { resumeRef.current = q; setBlocked(PRO_CHAPTER_MSG); }
    } catch (e) {
      setMessages(m => m.slice(0, -1));
      toast.error("Coach unavailable. Try again.");
    } finally { setSending(false); }
  };

  const makeMeme = async () => {
    const idea = input.trim();
    if (!idea) { toast.message("Type your meme idea first, then tap Make a meme."); return; }
    setInput("");
    await generateMeme(idea, `Make a meme: ${idea}`);
  };

  const downloadMeme = (dataUrl) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `ihatemyjob-meme-${Date.now()}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Meme saved.");
  };

  const shareMeme = async (dataUrl) => {
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `ihatemyjob-meme-${Date.now()}.jpg`, { type: blob.type || "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: "Made with IHateMyJob.app" });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return; // user cancelled the share sheet
    }
    downloadMeme(dataUrl);
  };

  const freeOut = usage && !usage.is_premium && usage.messages_remaining === 0;

  return (
    <div className="max-w-3xl mx-auto px-5 pt-4 sm:pt-6 flex flex-col" style={{ minHeight: "calc(100dvh - 140px)" }}>
      {handsFree && <VoiceConversation onClose={() => setHandsFree(false)} onLimit={setBlocked} />}
      <LimitDialog message={blocked} showUpgrade={!user?.is_premium} resume={resumeRef.current} onClose={() => setBlocked(null)} />
      <div className="flex items-center justify-between gap-3">
        <div className="eyebrow text-[#FD0F10]">AI COACH</div>
        <Link to="/work" className="text-xs font-mono-ui uppercase tracking-wider border border-[#2A2E39] px-3 py-1.5 rounded hover:border-white inline-flex items-center gap-1.5" data-testid="coach-my-work"><FolderOpen size={13} /> My Work</Link>
      </div>
      <h1 className={`font-display font-black mt-1 ${messages.length ? "text-xl sm:text-4xl" : "text-3xl sm:text-4xl"}`}>Your business coach. On your side.</h1>
      {messages.length === 0 && <p className="text-gray-300 text-sm mt-2">Ask anything, in plain words. Ideas, content, captions, memes, next steps. Every answer tells you what to do next.</p>}

      {!user?.is_premium && <VoiceSample className="mt-4" />}

      {user?.is_premium && (
        <>
          <button
            onClick={() => setHandsFree(true)}
            className="mt-4 flex items-center justify-center gap-2 w-full sm:w-auto self-start hero-accent text-white font-bold px-5 py-3 rounded-lg"
            data-testid="handsfree-start"
          >
            <Radio size={18} /> Talk hands-free
          </button>
          <p className="text-xs text-gray-500 mt-1.5">One tap to start. Just talk, your coach talks back. Tap the X to end.</p>
          {messages.length === 0 && <VoicePicker />}
        </>
      )}

      {messages.length === 0 && !streaming && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="coach-suggestions">
          {QUICK.map(s => (
            <button key={s} onClick={() => send(s)} className="text-left p-3 rounded-lg border border-[#2A2E39] bg-[#14161B] text-sm text-gray-200 hover:border-[#FD0F10]" data-testid={`suggest-${s.slice(0,15)}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      <div ref={scrollRef} className="mt-4 sm:mt-6 space-y-3 sm:space-y-4 flex-1 max-h-[60vh] overflow-y-auto pr-1" data-testid="coach-messages">
        {messages.map((m, i) => (
          <div key={i} className={`p-3 sm:p-4 rounded-lg ${m.role === "user" ? "bg-[#FD0F10]/10 border border-[#FD0F10]/30 ml-8 sm:ml-12" : "card-panel mr-3 sm:mr-6"}`}>
            <div className="eyebrow text-[#FD0F10]">{m.role === "user" ? "YOU" : "COACH"}</div>
            <div className="mt-1 text-sm sm:text-base text-gray-100 whitespace-pre-wrap leading-relaxed">{clean(m.content)}</div>
            {m.image && <img src={m.image} alt="Generated meme" className="mt-3 rounded-lg max-w-full border border-[#2A2E39]" data-testid="coach-meme-image" />}
            {m.image && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {m.prompt && (
                  <button
                    onClick={() => generateMeme(m.prompt, "Make another", !!m.character)}
                    disabled={sending}
                    className="flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider text-gray-300 border border-[#2A2E39] hover:border-[#FD0F10] hover:text-white px-3 py-1.5 rounded transition-colors disabled:opacity-50"
                    data-testid="coach-meme-again"
                  >
                    <RefreshCw size={13} /> Make another
                  </button>
                )}
                <button
                  onClick={() => downloadMeme(m.image)}
                  className="flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider text-gray-300 border border-[#2A2E39] hover:border-[#FD0F10] hover:text-white px-3 py-1.5 rounded transition-colors"
                  data-testid="coach-meme-download"
                >
                  <Download size={13} /> Download
                </button>
                <button
                  onClick={() => shareMeme(m.image)}
                  className="flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider text-white hero-accent px-3 py-1.5 rounded"
                  data-testid="coach-meme-share"
                >
                  <Share2 size={13} /> Share
                </button>
                {charMode && (
                  <button
                    onClick={() => saveAsCharacter(m.image)}
                    className="flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider text-gray-300 border border-[#2A2E39] hover:border-[#FD0F10] hover:text-white px-3 py-1.5 rounded transition-colors"
                    data-testid="coach-use-as-character"
                  >
                    Use as my character picture
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {streaming && (
          <div className="p-3 sm:p-4 rounded-lg card-panel mr-3 sm:mr-6">
            <div className="eyebrow text-[#FD0F10]">COACH</div>
            <div className="mt-1 text-sm sm:text-base text-gray-100 whitespace-pre-wrap leading-relaxed">{clean(finalText(streaming))}<span className="animate-pulse">▍</span></div>
            {reviewing(streaming) && <div className="mt-2 text-xs text-[#FD0F10] font-mono-ui uppercase tracking-wider animate-pulse" data-testid="coach-fact-checking">Fact-checking your chapter…</div>}
          </div>
        )}
        {sending && !streaming && <div className="text-xs text-gray-500 pl-1" data-testid="coach-working">Coach is working…</div>}
        {!user?.is_premium && !freeOut && !sending && sentCount % 5 === 3 && <CoachUpsellNote />}
        {localPath && historyReady && !sending && !service && (
          <ServicePicker key="first" onPicked={(s) => { setService(s); send(`I picked ${s}. What's my first step?`); }} />
        )}
        {localPath && !sending && service && switchAsked && (
          <ServicePicker key="switch" switching title="STILL WANT TO SWITCH? PICK A NEW SERVICE" onPicked={(s) => { setService(s); setSwitchAsked(false); send(`I've switched to ${s}. Let's start my new 7-day sprint.`); }} />
        )}
      </div>

      {freeOut ? (
        <div className="mt-4 text-center card-panel p-4 rounded-lg" data-testid="coach-free-out">
          <p className="text-sm text-gray-300" data-testid="coach-free-limit-msg">You've reached today's limit. Your coach is back tomorrow. Resets at midnight tonight, your local time. Or go Pro for more daily coaching at 19 USD/month.</p>
          <Link to="/pro" className="mt-3 inline-block hero-accent text-white font-bold px-6 py-2.5 rounded" data-testid="coach-upgrade">Upgrade to Pro</Link>
          <CoachUpsellNote testId="coach-limit-upsell-note" />
        </div>
      ) : (
        <form onSubmit={e => { e.preventDefault(); send(); }} className="mt-4 flex gap-2 sticky bottom-[calc(5rem+env(safe-area-inset-bottom))] bg-[#0B0C0E]/95 pt-2 pb-1">
          <input value={input} onChange={e => setInput(e.target.value)} placeholder="Type what you want, or a meme idea" className="flex-1 min-w-0 bg-[#14161B] border border-[#2A2E39] rounded-lg px-4 py-3 focus:border-[#FD0F10] outline-none" data-testid="coach-input" />
          <button type="button" onClick={makeMeme} disabled={sending} title="Make a meme" className="border border-[#2A2E39] text-gray-200 px-3 rounded-lg hover:border-[#FD0F10] disabled:opacity-50" data-testid="coach-meme">
            <ImagePlus size={18} />
          </button>
          {user?.is_premium && <VoiceButton disabled={sending} onTranscript={(t) => send(t)} />}
          <button disabled={sending || !input.trim()} className="hero-accent text-white px-4 rounded-lg disabled:opacity-50" data-testid="coach-send">
            <Send size={18}/>
          </button>
        </form>
      )}
    </div>
  );
}
